import { aiAnalyticsService } from './ai-analytics-service'
import { getAdminToken } from './api-service'
import { integrationService } from './integration-service'
import { hybridAIService } from './hybrid-ai-service'
import { aiPerformanceMonitor } from './ai-performance-monitor'
import { aiSystemInspectionService } from './ai-system-inspection-service'
import { aiAlertService } from './ai-alert-service'
import { localAIService } from './local-ai-service'
import { aiRecommendationEngine } from './ai-recommendation-engine'
import { aiOrderIntelligence } from './ai-order-intelligence'
import { aiImageAnalysis } from './ai-image-analysis'
import { aiAvatarVerification } from './ai-avatar-verification'

// 系统类型定义
export interface System {
  id: string
  name: string
  type: 'backend' | 'frontend' | 'mini-program' | 'mobile'
  status: 'online' | 'offline' | 'degraded'
  version: string
  url: string
  lastChecked: Date
  healthCheck: () => Promise<boolean>
  metadata?: Record<string, any>
}

// 学习数据类型定义
export interface LearningData {
  id: string
  type: 'user-interaction' | 'system-event' | 'feedback'
  content: any
  timestamp: Date
  userId?: string
  systemId?: string
  metadata?: Record<string, any>
}

// AI功能类型定义
export interface AIFeature {
  id: string
  name: string
  description: string
  status: 'online' | 'offline' | 'degraded'
  version: string
  type: 'assistant' | 'analytics' | 'prediction' | 'anomaly' | 'security' | 'content' | 'recommendation'
  dependencies?: string[]
  healthCheck: () => Promise<boolean>
  metadata?: Record<string, any>
}

// AI任务类型定义
export interface AITask {
  id: string
  name: string
  description: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  startTime?: Date
  endTime?: Date
  result?: any
  error?: string
  featureId: string
  metadata?: Record<string, any>
}

// AI学习模型类型定义
export interface LearningModel {
  id: string
  name: string
  type: 'supervised' | 'unsupervised' | 'reinforcement'
  accuracy: number
  lastTrained: Date
  trainingDataCount: number
  metadata?: Record<string, any>
}

// 谐伴AI核心管理服务
class AICoreService {
  private aiFeatures: Map<string, AIFeature> = new Map()
  private aiTasks: Map<string, AITask> = new Map()
  private taskQueue: string[] = []
  private isProcessingTasks = false
  private healthCheckInterval: number | null = null
  private userPreferences: Map<string, any> = new Map()
  private functionSynergyMap: Map<string, string[]> = new Map()
  private intelligentScheduler: any = null
  private eventBus: Map<string, ((...args: any[]) => void)[]> = new Map()
  private systemKnowledge: any = {
    capabilities: [],
    limitations: [],
    bestPractices: []
  }
  
  // 新增属性
  private systems: Map<string, System> = new Map()
  private learningData: Map<string, LearningData> = new Map()
  private learningModels: Map<string, LearningModel> = new Map()
  private systemInspectionInterval: number | null = null
  private vulnerabilityDatabase: Map<string, any> = new Map()

  constructor() {
    this.initializeHealthCheck()
    this.initializeIntelligentScheduler()
    this.initializeSystemKnowledge()
    this.initializeFunctionSynergy()
    this.initializeSystems()
    this.initializeLearningModels()
    this.initializeSystemInspection()
    this.initializeVulnerabilityDatabase()
  }

  // 初始化系统管理
  private initializeSystems() {
    // 注册默认系统
    this.registerSystem({
      id: 'backend-admin',
      name: '谐伴智能后台管理系统',
      type: 'backend',
      status: 'online',
      version: '1.0.0',
      url: window.location.origin,
      lastChecked: new Date(),
      healthCheck: async () => {
        return true
      }
    })

    this.registerSystem({
      id: 'frontend-web',
      name: '谐伴服务Web前端系统',
      type: 'frontend',
      status: 'online',
      version: '1.0.0',
      url: window.location.origin,
      lastChecked: new Date(),
      healthCheck: async () => {
        return true
      }
    })

    this.registerSystem({
      id: 'mini-program',
      name: '谐伴达人小程序系统',
      type: 'mini-program',
      status: 'online',
      version: '1.0.0',
      url: 'weixin://wx7a8b9c0d1e2f3g4h',
      lastChecked: new Date(),
      healthCheck: async () => {
        // 小程序健康检查逻辑
        return true
      }
    })
  }

  // 初始化学习模型
  private initializeLearningModels() {
    // 注册默认学习模型
    this.learningModels.set('user-interaction-model', {
      id: 'user-interaction-model',
      name: '用户交互学习模型',
      type: 'supervised',
      accuracy: 0.85,
      lastTrained: new Date(),
      trainingDataCount: 1000
    })

    this.learningModels.set('system-anomaly-model', {
      id: 'system-anomaly-model',
      name: '系统异常检测模型',
      type: 'unsupervised',
      accuracy: 0.90,
      lastTrained: new Date(),
      trainingDataCount: 5000
    })

    this.learningModels.set('decision-making-model', {
      id: 'decision-making-model',
      name: '决策优化模型',
      type: 'reinforcement',
      accuracy: 0.80,
      lastTrained: new Date(),
      trainingDataCount: 2000
    })

    // 添加新的AI模型
    // 自然语言处理模型
    this.learningModels.set('nlp-model', {
      id: 'nlp-model',
      name: '自然语言处理模型',
      type: 'supervised',
      accuracy: 0.92,
      lastTrained: new Date(),
      trainingDataCount: 8000,
      metadata: {
        capabilities: ['sentiment-analysis', 'entity-recognition', 'intent-classification', 'text-summarization']
      }
    })

    // 计算机视觉模型
    this.learningModels.set('computer-vision-model', {
      id: 'computer-vision-model',
      name: '计算机视觉模型',
      type: 'supervised',
      accuracy: 0.88,
      lastTrained: new Date(),
      trainingDataCount: 6000,
      metadata: {
        capabilities: ['object-detection', 'image-classification', 'face-recognition', 'optical-character-recognition']
      }
    })

    // 时间序列预测模型
    this.learningModels.set('time-series-model', {
      id: 'time-series-model',
      name: '时间序列预测模型',
      type: 'supervised',
      accuracy: 0.86,
      lastTrained: new Date(),
      trainingDataCount: 10000,
      metadata: {
        capabilities: ['sales-forecasting', 'demand-prediction', 'trend-analysis', 'anomaly-detection']
      }
    })

    // 聚类分析模型
    this.learningModels.set('clustering-model', {
      id: 'clustering-model',
      name: '聚类分析模型',
      type: 'unsupervised',
      accuracy: 0.83,
      lastTrained: new Date(),
      trainingDataCount: 7000,
      metadata: {
        capabilities: ['user-segmentation', 'behavioral-clustering', 'anomaly-detection', 'pattern-recognition']
      }
    })

    // 推荐系统模型
    this.learningModels.set('recommendation-model', {
      id: 'recommendation-model',
      name: '推荐系统模型',
      type: 'supervised',
      accuracy: 0.91,
      lastTrained: new Date(),
      trainingDataCount: 12000,
      metadata: {
        capabilities: ['collaborative-filtering', 'content-based-filtering', 'hybrid-recommendation', 'personalization']
      }
    })

    // 强化学习优化模型
    this.learningModels.set('reinforcement-optimization-model', {
      id: 'reinforcement-optimization-model',
      name: '强化学习优化模型',
      type: 'reinforcement',
      accuracy: 0.87,
      lastTrained: new Date(),
      trainingDataCount: 5000,
      metadata: {
        capabilities: ['resource-allocation', 'process-optimization', 'route-planning', 'inventory-management']
      }
    })
  }

  // 初始化系统巡查
  private initializeSystemInspection() {
    // 每小时执行一次系统巡查
    this.systemInspectionInterval = window.setInterval(() => {
      this.startSystemInspection()
    }, 3600000) // 1小时
  }

  // 初始化漏洞库
  private initializeVulnerabilityDatabase() {
    // 预加载常见漏洞
    this.vulnerabilityDatabase.set('XSS-001', {
      id: 'XSS-001',
      name: '跨站脚本攻击',
      severity: 'high',
      description: '未对用户输入进行适当过滤，可能导致恶意脚本执行',
      fix: '对用户输入进行HTML转义'
    })

    this.vulnerabilityDatabase.set('SQL-001', {
      id: 'SQL-001',
      name: 'SQL注入攻击',
      severity: 'critical',
      description: '未对SQL查询参数进行适当处理，可能导致数据库被攻击',
      fix: '使用参数化查询或预编译语句'
    })

    this.vulnerabilityDatabase.set('CSRF-001', {
      id: 'CSRF-001',
      name: '跨站请求伪造',
      severity: 'medium',
      description: '未验证请求来源，可能导致恶意网站代表用户执行操作',
      fix: '实现CSRF令牌验证'
    })
  }

  // 初始化智能调度器
  private initializeIntelligentScheduler() {
    this.intelligentScheduler = {
      prioritizeTask: (task: AITask): number => {
        // 基于任务类型和功能重要性进行优先级排序
        const priorityMap: Record<string, number> = {
          'assistant': 10,
          'security': 9,
          'anomaly': 8,
          'prediction': 7,
          'analytics': 6,
          'content': 5,
          'recommendation': 4
        }
        
        const feature = this.aiFeatures.get(task.featureId)
        return feature ? (priorityMap[feature.type] || 5) : 5
      },
      optimizeExecution: (tasks: AITask[]) => {
        // 优化任务执行顺序
        return tasks.sort((a, b) => 
          this.intelligentScheduler.prioritizeTask(b) - this.intelligentScheduler.prioritizeTask(a)
        )
      }
    }
  }

  // 初始化系统知识
  private initializeSystemKnowledge() {
    this.systemKnowledge = {
      capabilities: [
        '智能语音交互',
        '数据分析和预测',
        '异常检测和警报',
        '安全防护和监控',
        '内容分析和处理',
        '个性化推荐',
        '订单智能处理',
        '系统导航和控制'
      ],
      limitations: [
        '依赖网络连接',
        '语音识别受环境噪音影响',
        '复杂任务处理时间较长',
        '预测准确性依赖数据质量',
        '部分功能需要用户授权'
      ],
      bestPractices: [
        '使用清晰简洁的语言进行交互',
        '在安静环境中使用语音功能',
        '定期更新系统以获得最佳性能',
        '提供反馈以帮助系统改进',
        '合理使用系统资源，避免过度请求'
      ]
    }
  }

  // 初始化功能协同映射
  private initializeFunctionSynergy() {
    this.functionSynergyMap.set('ai_assistant', ['ai_analytics', 'ai_prediction', 'ai_recommendation'])
    this.functionSynergyMap.set('ai_analytics', ['ai_prediction', 'ai_recommendation'])
    this.functionSynergyMap.set('ai_prediction', ['ai_analytics'])
    this.functionSynergyMap.set('ai_anomaly', ['ai_security'])
    this.functionSynergyMap.set('ai_security', ['ai_anomaly'])
    this.functionSynergyMap.set('ai_content', ['ai_analytics'])
    this.functionSynergyMap.set('ai_recommendation', ['ai_analytics', 'ai_prediction'])
  }

  // 注册AI功能
  registerFeature(feature: AIFeature): boolean {
    if (this.aiFeatures.has(feature.id)) {
      console.warn(`AI feature ${feature.id} already registered`)
      return false
    }

    this.aiFeatures.set(feature.id, feature)
    console.log(`AI feature ${feature.name} (${feature.id}) registered successfully`)

    // 初始化功能状态（未登录时不发起需鉴权的探测，避免登录页 401；登录后由定时健康检查统一执行）
    if (getAdminToken()) {
      this.healthCheckFeature(feature.id)
    }

    return true
  }

  // 注销AI功能
  unregisterFeature(featureId: string): boolean {
    if (!this.aiFeatures.has(featureId)) {
      console.warn(`AI feature ${featureId} not found`)
      return false
    }

    this.aiFeatures.delete(featureId)
    console.log(`AI feature ${featureId} unregistered successfully`)
    return true
  }

  // 获取所有AI功能
  getFeatures(): AIFeature[] {
    return Array.from(this.aiFeatures.values())
  }

  // 根据类型获取AI功能
  getFeaturesByType(type: AIFeature['type']): AIFeature[] {
    return Array.from(this.aiFeatures.values()).filter(feature => feature.type === type)
  }

  // 获取单个AI功能
  getFeature(featureId: string): AIFeature | undefined {
    return this.aiFeatures.get(featureId)
  }

  // 启动AI任务
  async startTask(task: Omit<AITask, 'id' | 'status' | 'progress' | 'startTime' | 'endTime'>): Promise<AITask> {
    const newTask: AITask = {
      ...task,
      id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending',
      progress: 0,
      startTime: new Date()
    }

    this.aiTasks.set(newTask.id, newTask)
    this.taskQueue.push(newTask.id)

    console.log(`Task ${newTask.id} queued: ${newTask.name}`)

    // 开始处理任务队列
    this.processTaskQueue()

    return newTask
  }

  // 获取任务
  getTask(taskId: string): AITask | undefined {
    return this.aiTasks.get(taskId)
  }

  // 获取所有任务
  getTasks(): AITask[] {
    return Array.from(this.aiTasks.values())
  }

  // 获取任务队列
  getTaskQueue(): string[] {
    return [...this.taskQueue]
  }

  // 取消任务
  cancelTask(taskId: string): boolean {
    const task = this.aiTasks.get(taskId)
    if (!task) {
      return false
    }

    if (task.status === 'pending') {
      // 从队列中移除
      const index = this.taskQueue.indexOf(taskId)
      if (index > -1) {
        this.taskQueue.splice(index, 1)
      }
    }

    task.status = 'failed'
    task.error = 'Task cancelled'
    task.endTime = new Date()

    return true
  }

  // 处理任务队列
  private async processTaskQueue() {
    if (this.isProcessingTasks || this.taskQueue.length === 0) {
      return
    }

    this.isProcessingTasks = true

    // 获取所有待处理任务
    const pendingTasks: AITask[] = []
    while (this.taskQueue.length > 0) {
      const taskId = this.taskQueue.shift()
      if (taskId) {
        const task = this.aiTasks.get(taskId)
        if (task) {
          pendingTasks.push(task)
        }
      }
    }

    // 使用智能调度器优化任务执行顺序
    const optimizedTasks = this.intelligentScheduler.optimizeExecution(pendingTasks)

    // 执行优化后的任务队列
    for (const task of optimizedTasks) {
      try {
        task.status = 'running'
        console.log(`Processing task ${task.id}: ${task.name}`)
        
        // 触发任务开始事件
        this.emitEvent('task-started', task)

        // 执行任务
        await this.executeTask(task)

        task.status = 'completed'
        task.progress = 100
        task.endTime = new Date()
        console.log(`Task ${task.id} completed successfully`)
        
        // 触发任务完成事件
        this.emitEvent('task-completed', task)
        
        // 检查功能协同
        await this.checkFunctionSynergy(task)
      } catch (error) {
        task.status = 'failed'
        task.error = error instanceof Error ? error.message : String(error)
        task.endTime = new Date()
        console.error(`Task ${task.id} failed:`, error)
        
        // 触发任务失败事件
        this.emitEvent('task-failed', task)
      }
    }

    this.isProcessingTasks = false
  }

  // 检查功能协同
  private async checkFunctionSynergy(task: AITask) {
    const synergisticFeatures = this.functionSynergyMap.get(task.featureId)
    if (synergisticFeatures) {
      for (const featureId of synergisticFeatures) {
        const feature = this.aiFeatures.get(featureId)
        if (feature && feature.status === 'online') {
          console.log(`Triggering synergistic feature ${feature.name} for task ${task.id}`)
          // 这里可以触发协同功能的相关操作
        }
      }
    }
  }

  // 事件总线：订阅事件
  subscribeEvent(event: string, callback: (...args: any[]) => void) {
    if (!this.eventBus.has(event)) {
      this.eventBus.set(event, [])
    }
    this.eventBus.get(event)?.push(callback)
  }

  // 事件总线：触发事件
  emitEvent(event: string, ...args: any[]) {
    const callbacks = this.eventBus.get(event)
    if (callbacks) {
      for (const callback of callbacks) {
        try {
          callback(...args)
        } catch (error) {
          console.error(`Error in event callback for ${event}:`, error)
        }
      }
    }
  }

  // 事件总线：取消订阅
  unsubscribeEvent(event: string, callback: (...args: any[]) => void) {
    const callbacks = this.eventBus.get(event)
    if (callbacks) {
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  // 用户偏好管理：设置用户偏好
  setUserPreference(userId: string, key: string, value: any) {
    if (!this.userPreferences.has(userId)) {
      this.userPreferences.set(userId, {})
    }
    const preferences = this.userPreferences.get(userId)
    preferences[key] = value
    this.userPreferences.set(userId, preferences)
    
    // 触发偏好更新事件
    this.emitEvent('preference-updated', { userId, key, value })
  }

  // 用户偏好管理：获取用户偏好
  getUserPreference(userId: string, key?: string) {
    const preferences = this.userPreferences.get(userId)
    if (!preferences) {
      return undefined
    }
    return key ? preferences[key] : preferences
  }

  // 用户偏好管理：获取所有用户偏好
  getAllUserPreferences() {
    return Object.fromEntries(this.userPreferences)
  }

  // 获取系统知识
  getSystemKnowledge() {
    return this.systemKnowledge
  }

  // 获取功能协同映射
  getFunctionSynergyMap() {
    return Object.fromEntries(this.functionSynergyMap)
  }

  // 执行任务
  private async executeTask(task: AITask): Promise<void> {
    const feature = this.aiFeatures.get(task.featureId)
    if (!feature) {
      throw new Error(`Feature ${task.featureId} not found`)
    }

    // 根据功能类型执行不同的任务
    switch (feature.type) {
      case 'assistant':
        await this.executeAssistantTask(task)
        break
      case 'analytics':
        await this.executeAnalyticsTask(task)
        break
      case 'prediction':
        await this.executePredictionTask(task)
        break
      case 'anomaly':
        await this.executeAnomalyTask(task)
        break
      case 'security':
        await this.executeSecurityTask(task)
        break
      case 'content':
        await this.executeContentTask(task)
        break
      case 'recommendation':
        await this.executeRecommendationTask(task)
        break
      default:
        throw new Error(`Unknown feature type: ${feature.type}`)
    }
  }

  // 执行助手任务
  private async executeAssistantTask(task: AITask): Promise<void> {
    const userQuery = task.metadata?.query || task.description
    const conversationHistory = task.metadata?.history || []
    
    try {
      // 构建消息历史
      const messages: DeepSeekMessage[] = [
        {
          role: 'system',
          content: '你是谐伴AI助手，一个专业的后台管理系统智能助手。请使用中文进行回复，保持友好、专业的语气。'
        },
        ...conversationHistory.map((msg: any) => ({
          role: msg.role,
          content: msg.content
        })),
        {
          role: 'user',
          content: userQuery
        }
      ]

      // 调用混合AI服务
      const aiResponse = await hybridAIService.chat(messages.map(m => m.content).join('\n'))

      task.result = {
        response: aiResponse,
        confidence: 0.95,
        timestamp: new Date()
      }
      task.progress = 100
    } catch (error) {
      console.error('DeepSeek API call failed:', error)
      // 回退到模拟响应
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 200))
        task.progress = i
      }
      task.result = {
        response: '这是谐伴AI助手的响应。由于模型服务暂时不可用，我将为您提供本地响应。',
        confidence: 0.85,
        timestamp: new Date(),
        fallback: true
      }
    }
  }

  // 执行分析任务
  private async executeAnalyticsTask(task: AITask): Promise<void> {
    const analyticsType = task.metadata?.analyticsType
    let rawData: any
    
    switch (analyticsType) {
      case 'userBehavior':
        rawData = await aiAnalyticsService.getUserBehaviorAnalysis()
        break
      case 'business':
        rawData = await aiAnalyticsService.getBusinessAnalysis()
        break
      case 'prediction':
        rawData = await aiAnalyticsService.getPredictionAnalysis()
        break
      case 'trend':
        rawData = await aiAnalyticsService.getTrendAnalysis()
        break
      default:
        rawData = await aiAnalyticsService.getAnalyticsOverview()
    }

    try {
      // 使用DeepSeek模型进行智能分析
      const analysisPrompt = this.getAnalyticsPrompt(analyticsType)
      const aiAnalysis = await hybridAIService.analyzeData(rawData, analysisPrompt)
      
      task.result = {
        rawData,
        aiAnalysis,
        analysisType: analyticsType,
        timestamp: new Date()
      }
    } catch (error) {
      console.error('DeepSeek analysis failed:', error)
      task.result = {
        rawData,
        aiAnalysis: 'AI分析服务暂时不可用，显示原始数据',
        analysisType: analyticsType,
        timestamp: new Date(),
        fallback: true
      }
    }

    task.progress = 100
  }

  private getAnalyticsPrompt(analyticsType?: string): string {
    const prompts: Record<string, string> = {
      userBehavior: '请对用户行为数据进行深入分析，包括用户活跃度、行为模式、转化率等关键指标，并提供优化建议。',
      business: '请对业务数据进行全面分析，包括收入趋势、订单分析、客户增长等，并提供业务洞察和建议。',
      prediction: '请分析预测数据，评估预测准确性，识别趋势变化，并提供决策建议。',
      trend: '请分析趋势数据，识别增长或下降趋势，预测未来发展方向，并提供战略建议。'
    }
    return prompts[analyticsType || ''] || '请对数据进行综合分析，提供关键洞察和建议。'
  }

  // 执行预测任务
  private async executePredictionTask(task: AITask): Promise<void> {
    const predictionType = task.metadata?.predictionType || 'sales'
    const historicalData = task.metadata?.historicalData || {}
    
    try {
      const prompt = `请基于历史数据进行${this.getPredictionTypeName(predictionType)}预测。
历史数据：${JSON.stringify(historicalData)}

请提供：
1. 预测结果（数值和范围）
2. 置信度评估
3. 影响因素分析
4. 建议和注意事项`

      const predictionResult = await hybridAIService.generateReport(historicalData, predictionType)
      
      task.result = {
        prediction: predictionResult,
        model: 'DeepSeek-V4-Flash',
        timestamp: new Date(),
        confidence: 0.88
      }
    } catch (error) {
      console.error('DeepSeek prediction failed:', error)
      // 回退到模拟预测
      for (let i = 0; i <= 100; i += 20) {
        await new Promise(resolve => setTimeout(resolve, 300))
        task.progress = i
      }
      task.result = {
        prediction: {
          // V1.3.63 真实预测服务失败且无本地模型，不随机编造预测值
          value: 0,
          confidence: 0,
          range: null
        },
        model: 'linear_regression_v1 (fallback)',
        timestamp: new Date(),
        fallback: true
      }
    }
    task.progress = 100
  }

  private getPredictionTypeName(type: string): string {
    const types: Record<string, string> = {
      sales: '销售',
      demand: '需求',
      trend: '趋势',
      userGrowth: '用户增长',
      systemFailure: '系统故障'
    }
    return types[type] || type
  }

  // 执行异常检测任务
  private async executeAnomalyTask(task: AITask): Promise<void> {
    const detectionType = task.metadata?.detectionType || 'system'
    const systemLogs = task.metadata?.systemLogs || []
    
    try {
      const prompt = `请分析以下系统日志，检测异常行为。
检测类型：${detectionType}
系统日志：${JSON.stringify(systemLogs)}

请识别：
1. 异常事件列表（包含类型、严重程度、描述）
2. 异常原因分析
3. 建议的处理措施`

      const anomalyAnalysis = await hybridAIService.analyzeData(systemLogs, prompt)
      
      task.result = {
        anomalies: anomalyAnalysis,
        scannedItems: systemLogs.length || 1500,
        falsePositives: 0,
        model: 'DeepSeek-V4-Flash',
        timestamp: new Date()
      }
    } catch (error) {
      console.error('DeepSeek anomaly detection failed:', error)
      // 回退到模拟检测
      for (let i = 0; i <= 100; i += 15) {
        await new Promise(resolve => setTimeout(resolve, 250))
        task.progress = i
      }
      task.result = {
        anomalies: [
          {
            id: 'anomaly_1',
            type: 'suspicious_activity',
            severity: 'medium',
            description: 'Unusual login pattern detected',
            timestamp: new Date()
          }
        ],
        scannedItems: 1500,
        falsePositives: 5,
        fallback: true
      }
    }
    task.progress = 100
  }

  // 执行安全任务
  private async executeSecurityTask(task: AITask): Promise<void> {
    const securityLogs = task.metadata?.securityLogs || []
    
    try {
      const prompt = `请分析以下安全日志，进行风险评估和漏洞检测。
安全日志：${JSON.stringify(securityLogs)}

请提供：
1. 安全评分
2. 识别的漏洞列表（包含ID、严重程度、描述、修复建议）
3. 安全改进建议`

      const securityAnalysis = await deepseekService.generateReport(securityLogs, 'security')
      
      task.result = {
        securityScore: 90,
        vulnerabilities: securityAnalysis,
        scannedServices: 12,
        secureServices: 12,
        model: 'DeepSeek-V4-Flash',
        timestamp: new Date()
      }
    } catch (error) {
      console.error('DeepSeek security analysis failed:', error)
      // 回退到模拟结果
      for (let i = 0; i <= 100; i += 25) {
        await new Promise(resolve => setTimeout(resolve, 350))
        task.progress = i
      }
      task.result = {
        securityScore: 85,
        vulnerabilities: [
          {
            id: 'vuln_1',
            severity: 'low',
            description: 'Outdated library detected',
            recommendation: 'Update to latest version'
          }
        ],
        scannedServices: 12,
        secureServices: 10,
        fallback: true
      }
    }
    task.progress = 100
  }

  // 执行内容任务
  private async executeContentTask(task: AITask): Promise<void> {
    const content = task.metadata?.content || ''
    const contentTypes = task.metadata?.contentTypes || ['reviews', 'social']
    
    try {
      const prompt = `请分析以下内容，进行情感分析和主题提取。
内容：${content}
内容类型：${contentTypes.join('、')}

请提供：
1. 情感分析结果
2. 提取的主题和实体
3. 内容摘要`

      const contentAnalysis = await deepseekService.analyzeData({ content, contentTypes }, prompt)
      
      task.result = {
        analysis: contentAnalysis,
        confidence: 0.95,
        processingTime: 0.5,
        model: 'DeepSeek-V4-Flash'
      }
    } catch (error) {
      console.error('DeepSeek content analysis failed:', error)
      // 回退到模拟分析
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 200))
        task.progress = i
      }
      task.result = {
        analysis: {
          sentiment: 'positive',
          topics: ['technology', 'ai', 'innovation'],
          entities: ['AI assistant', 'machine learning']
        },
        confidence: 0.92,
        processingTime: 1.2,
        fallback: true
      }
    }
    task.progress = 100
  }

  // 执行推荐任务
  private async executeRecommendationTask(task: AITask): Promise<void> {
    const userProfile = task.metadata?.userProfile || {}
    const userHistory = task.metadata?.userHistory || []
    const recommendationType = task.metadata?.recommendationType || 'services'
    
    try {
      const prompt = `请基于用户资料和历史记录，提供${this.getRecommendationTypeName(recommendationType)}推荐。
用户资料：${JSON.stringify(userProfile)}
历史记录：${JSON.stringify(userHistory)}

请提供：
1. 推荐项列表（包含ID、类型、名称、推荐分数、推荐理由）
2. 推荐依据分析
3. 个性化程度评估`

      const recommendationResult = await hybridAIService.generateReport({ userProfile, userHistory }, 'user')
      
      task.result = {
        recommendations: recommendationResult,
        personalized: true,
        generatedAt: new Date(),
        model: 'DeepSeek-V4-Flash'
      }
    } catch (error) {
      console.error('DeepSeek recommendation failed:', error)
      // 回退到模拟推荐
      for (let i = 0; i <= 100; i += 20) {
        await new Promise(resolve => setTimeout(resolve, 280))
        task.progress = i
      }
      task.result = {
        recommendations: [
          {
            id: 'rec_1',
            type: 'service',
            name: 'AI Analytics',
            score: 0.95,
            reason: 'Based on your recent usage patterns'
          },
          {
            id: 'rec_2',
            type: 'service',
            name: 'Security Monitoring',
            score: 0.88,
            reason: 'High relevance to your business needs'
          }
        ],
        personalized: true,
        generatedAt: new Date(),
        fallback: true
      }
    }
    task.progress = 100
  }

  private getRecommendationTypeName(type: string): string {
    const types: Record<string, string> = {
      services: '服务',
      products: '产品',
      content: '内容',
      personalization: '个性化',
      systemOptimization: '系统优化'
    }
    return types[type] || type
  }

  // 健康检查单个功能
  async healthCheckFeature(featureId: string): Promise<boolean> {
    const feature = this.aiFeatures.get(featureId)
    if (!feature) {
      return false
    }

    try {
      const isHealthy = await feature.healthCheck()
      feature.status = isHealthy ? 'online' : 'degraded'
      return isHealthy
    } catch (error) {
      console.error(`Health check failed for feature ${featureId}:`, error)
      feature.status = 'offline'
      return false
    }
  }

  // 健康检查所有功能
  async healthCheckAllFeatures(): Promise<Map<string, boolean>> {
    const results = new Map<string, boolean>()

    // 未登录（登录页/退出后）时跳过：健康检查会请求需鉴权的真实接口，
    // 此时只会产生无意义的 401 请求刷屏。登录后下一个 30 秒周期自动恢复。
    if (!getAdminToken()) {
      return results
    }

    for (const [id] of this.aiFeatures) {
      const result = await this.healthCheckFeature(id)
      results.set(id, result)
    }

    return results
  }

  // 初始化健康检查
  private initializeHealthCheck() {
    this.healthCheckInterval = window.setInterval(() => {
      this.healthCheckAllFeatures()
    }, 30000) // 每30秒执行一次健康检查
  }

  // 关闭健康检查
  shutdownHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
    }
  }

  // 获取系统健康状态
  getSystemHealthStatus(): {
    status: 'healthy' | 'degraded' | 'critical'
    onlineCount: number
    totalCount: number
    details: Map<string, AIFeature['status']>
  } {
    const features = this.getFeatures()
    const totalCount = features.length
    const onlineCount = features.filter(f => f.status === 'online').length
    const details = new Map(features.map(f => [f.id, f.status]))

    let status: 'healthy' | 'degraded' | 'critical' = 'healthy'
    if (onlineCount === 0) {
      status = 'critical'
    } else if (onlineCount < totalCount) {
      status = 'degraded'
    }

    return {
      status,
      onlineCount,
      totalCount,
      details
    }
  }

  // 重启功能
  async restartFeature(featureId: string): Promise<boolean> {
    const feature = this.aiFeatures.get(featureId)
    if (!feature) {
      return false
    }

    try {
      feature.status = 'offline'
      console.log(`Restarting AI feature ${feature.name}...`)
      
      // 模拟重启过程
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const isHealthy = await feature.healthCheck()
      feature.status = isHealthy ? 'online' : 'degraded'
      
      console.log(`AI feature ${feature.name} restarted with status: ${feature.status}`)
      return isHealthy
    } catch (error) {
      console.error(`Failed to restart feature ${featureId}:`, error)
      feature.status = 'offline'
      return false
    }
  }

  // 获取功能依赖图
  getFeatureDependencyGraph(): {
    nodes: { id: string; name: string; status: AIFeature['status'] }[]
    edges: { source: string; target: string }[]
  } {
    const nodes = Array.from(this.aiFeatures.values()).map(feature => ({
      id: feature.id,
      name: feature.name,
      status: feature.status
    }))

    const edges: { source: string; target: string }[] = []
    
    for (const [id, feature] of this.aiFeatures) {
      if (feature.dependencies) {
        for (const depId of feature.dependencies) {
          edges.push({ source: id, target: depId })
        }
      }
    }

    return { nodes, edges }
  }

  // 系统管理方法

  // 注册系统
  registerSystem(system: System): boolean {
    if (this.systems.has(system.id)) {
      console.warn(`System ${system.id} already registered`)
      return false
    }

    this.systems.set(system.id, system)
    console.log(`System ${system.name} (${system.id}) registered successfully`)
    
    // 初始化系统状态
    this.healthCheckSystem(system.id)
    
    return true
  }

  // 注销系统
  unregisterSystem(systemId: string): boolean {
    if (!this.systems.has(systemId)) {
      console.warn(`System ${systemId} not found`)
      return false
    }

    this.systems.delete(systemId)
    console.log(`System ${systemId} unregistered successfully`)
    return true
  }

  // 获取所有系统
  getSystems(): System[] {
    return Array.from(this.systems.values())
  }

  // 根据类型获取系统
  getSystemsByType(type: System['type']): System[] {
    return Array.from(this.systems.values()).filter(system => system.type === type)
  }

  // 获取单个系统
  getSystem(systemId: string): System | undefined {
    return this.systems.get(systemId)
  }

  // 健康检查单个系统
  async healthCheckSystem(systemId: string): Promise<boolean> {
    const system = this.systems.get(systemId)
    if (!system) {
      return false
    }

    try {
      const isHealthy = await system.healthCheck()
      system.status = isHealthy ? 'online' : 'degraded'
      system.lastChecked = new Date()
      return isHealthy
    } catch (error) {
      console.error(`Health check failed for system ${systemId}:`, error)
      system.status = 'offline'
      system.lastChecked = new Date()
      return false
    }
  }

  // 健康检查所有系统
  async healthCheckAllSystems(): Promise<Map<string, boolean>> {
    const results = new Map<string, boolean>()
    
    for (const [id] of this.systems) {
      const result = await this.healthCheckSystem(id)
      results.set(id, result)
    }

    return results
  }

  // 学习功能方法

  // 添加学习数据
  addLearningData(data: Omit<LearningData, 'id' | 'timestamp'>): LearningData {
    const newData: LearningData = {
      ...data,
      id: `learning_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    }

    this.learningData.set(newData.id, newData)
    console.log(`Learning data added: ${newData.type}`)
    
    // 触发学习数据添加事件
    this.emitEvent('learning-data-added', newData)

    return newData
  }

  // 获取学习数据
  getLearningData(dataId: string): LearningData | undefined {
    return this.learningData.get(dataId)
  }

  // 获取所有学习数据
  getLearningDataByType(type: LearningData['type']): LearningData[] {
    return Array.from(this.learningData.values()).filter(data => data.type === type)
  }

  // 训练学习模型
  async trainLearningModel(modelId: string, dataIds: string[]): Promise<boolean> {
    const model = this.learningModels.get(modelId)
    if (!model) {
      console.error(`Model ${modelId} not found`)
      return false
    }

    try {
      console.log(`Training model ${model.name} with ${dataIds.length} data points`)
      
      // 模拟训练过程
      await new Promise(resolve => setTimeout(resolve, 5000))
      
      // 更新模型信息
      model.lastTrained = new Date()
      model.trainingDataCount += dataIds.length
      model.accuracy = Math.min(0.99, model.accuracy + 0.01)
      
      console.log(`Model ${model.name} trained successfully. New accuracy: ${model.accuracy}`)
      
      // 触发模型训练完成事件
      this.emitEvent('model-trained', model)
      
      return true
    } catch (error) {
      console.error(`Training failed for model ${modelId}:`, error)
      return false
    }
  }

  // 获取所有学习模型
  getLearningModels(): LearningModel[] {
    return Array.from(this.learningModels.values())
  }

  // 系统巡查方法

  // 开始系统巡查
  async startSystemInspection(): Promise<void> {
    console.log('Starting system inspection...')
    
    const systems = this.getSystems()
    const inspectionResults = []
    
    for (const system of systems) {
      console.log(`Inspecting system: ${system.name}`)
      
      // 健康检查
      const isHealthy = await this.healthCheckSystem(system.id)
      
      // 漏洞扫描
      const vulnerabilities = await this.scanSystemVulnerabilities(system.id)
      
      // 性能检查
      const performance = await this.checkSystemPerformance(system.id)
      
      inspectionResults.push({
        systemId: system.id,
        systemName: system.name,
        isHealthy,
        vulnerabilities,
        performance,
        timestamp: new Date()
      })
      
      // 如果发现严重漏洞，尝试修复
      for (const vuln of vulnerabilities) {
        if (vuln.severity === 'critical' || vuln.severity === 'high') {
          await this.attemptFixVulnerability(system.id, vuln.id)
        }
      }
    }
    
    console.log('System inspection completed', inspectionResults)
    
    // 触发系统巡查完成事件
    this.emitEvent('system-inspection-completed', inspectionResults)
  }

  // 扫描系统漏洞
  async scanSystemVulnerabilities(systemId: string): Promise<any[]> {
    const system = this.systems.get(systemId)
    if (!system) {
      return []
    }

    console.log(`Scanning vulnerabilities for system: ${system.name}`)

    // V1.3.63 前端无真实漏洞扫描能力，原先从漏洞库随机抽取 1-2 条虚构扫描结果。
    // 无真实扫描工具接入时返回空列表（count=0），不虚构漏洞。
    return []
  }

  // 检查系统性能
  async checkSystemPerformance(systemId: string): Promise<any> {
    const system = this.systems.get(systemId)
    if (!system) {
      return null
    }

    console.log(`Checking performance for system: ${system.name}`)

    // V1.3.63 浏览器端无真实系统性能采集源（CPU/内存/磁盘/网络均为目标系统指标，
    // 非当前页面数据），原先全部 Math.random 生成属伪造监控。统一置 null 表示无采集源；
    // 待接入真实监控端点后由服务端数据填充。
    return {
      responseTime: null,
      cpuUsage: null,
      memoryUsage: null,
      diskUsage: null,
      networkLatency: null
    }
  }

  // 尝试修复漏洞
  async attemptFixVulnerability(systemId: string, vulnerabilityId: string): Promise<boolean> {
    const system = this.systems.get(systemId)
    const vulnerability = this.vulnerabilityDatabase.get(vulnerabilityId)
    
    if (!system || !vulnerability) {
      return false
    }

    console.log(`Attempting to fix vulnerability ${vulnerability.name} on system ${system.name}`)
    
    try {
      // 模拟修复过程
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      console.log(`Vulnerability ${vulnerability.name} fixed successfully on system ${system.name}`)
      
      // 触发漏洞修复事件
      this.emitEvent('vulnerability-fixed', {
        systemId,
        systemName: system.name,
        vulnerabilityId,
        vulnerabilityName: vulnerability.name,
        timestamp: new Date()
      })
      
      return true
    } catch (error) {
      console.error(`Failed to fix vulnerability ${vulnerabilityId} on system ${systemId}:`, error)
      return false
    }
  }

  // 前端AI集成方法

  // 发送指令到前端AI
  async sendCommandToFrontendAI(aiId: string, command: string, parameters?: any): Promise<boolean> {
    console.log(`Sending command to frontend AI ${aiId}: ${command}`, parameters)
    
    try {
      // 使用集成服务发送指令
      const result = await integrationService.callService('frontend-ai', 'sendCommand', {
        aiId,
        command,
        parameters
      })
      
      if (result.success) {
        console.log(`Command sent successfully to frontend AI ${aiId}`, result.data)
        
        // 触发前端AI指令发送事件
        this.emitEvent('frontend-ai-command-sent', {
          aiId,
          command,
          parameters,
          timestamp: new Date(),
          result: result.data
        })
        
        return true
      } else {
        console.error(`Failed to send command to frontend AI ${aiId}:`, result.error)
        return false
      }
    } catch (error) {
      console.error(`Failed to send command to frontend AI ${aiId}:`, error)
      return false
    }
  }

  // 获取前端AI状态
  async getFrontendAIStatus(aiId: string): Promise<any> {
    console.log(`Getting status for frontend AI ${aiId}`)
    
    try {
      // 使用集成服务获取状态
      const result = await integrationService.callService('frontend-ai', 'getStatus', {
        aiId
      })
      
      if (result.success) {
        return result.data
      } else {
        console.error(`Failed to get status for frontend AI ${aiId}:`, result.error)
        // 返回默认状态
        return {
          aiId,
          status: 'offline',
          lastActive: new Date(),
          version: '1.0.0',
          capabilities: []
        }
      }
    } catch (error) {
      console.error(`Failed to get status for frontend AI ${aiId}:`, error)
      // 返回默认状态
      return {
        aiId,
        status: 'offline',
        lastActive: new Date(),
        version: '1.0.0',
        capabilities: []
      }
    }
  }

  // 获取所有前端AI
  async getFrontendAIs(): Promise<any[]> {
    console.log('Getting all frontend AIs')
    
    try {
      // 使用集成服务获取所有前端AI
      const result = await integrationService.callService('frontend-ai', 'getFrontendAIs', {})
      
      if (result.success) {
        return result.data
      } else {
        console.error('Failed to get frontend AIs:', result.error)
        return []
      }
    } catch (error) {
      console.error('Failed to get frontend AIs:', error)
      return []
    }
  }

  // 同步系统状态到前端AI
  async syncSystemStatusToFrontendAIs(systemStatus: any): Promise<boolean> {
    console.log('Syncing system status to all frontend AIs')
    
    try {
      // 获取所有前端AI
      const frontEndAIs = await this.getFrontendAIs()
      const aiIds = frontEndAIs.map(ai => ai.aiId)
      
      // 批量发送系统状态
      const result = await integrationService.callService('frontend-ai', 'batchSendCommand', {
        aiIds,
        command: 'sync-system-status',
        parameters: { systemStatus }
      })
      
      if (result.success) {
        console.log('System status synced to frontend AIs successfully', result.data)
        
        // 触发系统状态同步事件
        this.emitEvent('system-status-synced-to-frontend', {
          timestamp: new Date(),
          result: result.data
        })
        
        return true
      } else {
        console.error('Failed to sync system status to frontend AIs:', result.error)
        return false
      }
    } catch (error) {
      console.error('Failed to sync system status to frontend AIs:', error)
      return false
    }
  }

  // 重启前端AI
  async restartFrontendAI(aiId: string): Promise<boolean> {
    console.log(`Restarting frontend AI ${aiId}`)
    
    try {
      // 使用集成服务重启前端AI
      const result = await integrationService.callService('frontend-ai', 'restartAI', {
        aiId
      })
      
      if (result.success) {
        console.log(`Frontend AI ${aiId} restarted successfully`, result.data)
        
        // 触发前端AI重启事件
        this.emitEvent('frontend-ai-restarted', {
          aiId,
          timestamp: new Date(),
          result: result.data
        })
        
        return true
      } else {
        console.error(`Failed to restart frontend AI ${aiId}:`, result.error)
        return false
      }
    } catch (error) {
      console.error(`Failed to restart frontend AI ${aiId}:`, error)
      return false
    }
  }

  // 关闭系统巡查
  shutdownSystemInspection() {
    if (this.systemInspectionInterval) {
      clearInterval(this.systemInspectionInterval)
      this.systemInspectionInterval = null
    }
  }

  // 关闭所有服务
  shutdown() {
    this.shutdownHealthCheck()
    this.shutdownSystemInspection()
    console.log('AI Core Service shutdown completed')
  }
}

// 导出单例实例
export const aiCoreService = new AICoreService()

// 全局AI功能注册函数
export function registerAIFeatures() {
  // 注册AI助手功能
  aiCoreService.registerFeature({
    id: 'ai_assistant',
    name: '谐伴AI助手',
    description: '智能AI助手，支持文字和语音交互',
    status: 'online',
    version: '2.0.0',
    type: 'assistant',
    healthCheck: async () => {
      return true // 模拟健康检查
    },
    metadata: {
      capabilities: ['text', 'voice', 'navigation', 'analysis', 'learning', 'system-management']
    }
  })

  // 注册AI分析功能
  aiCoreService.registerFeature({
    id: 'ai_analytics',
    name: 'AI智能分析',
    description: '智能数据分析和预测',
    status: 'online',
    version: '2.0.0',
    type: 'analytics',
    healthCheck: async () => {
      try {
        await aiAnalyticsService.getAnalyticsOverview()
        return true
      } catch {
        return false
      }
    },
    metadata: {
      analysisTypes: ['userBehavior', 'business', 'prediction', 'trend', 'systemPerformance']
    }
  })

  // 注册AI预测功能
  aiCoreService.registerFeature({
    id: 'ai_prediction',
    name: 'AI预测分析',
    description: '基于历史数据的智能预测',
    status: 'online',
    version: '2.0.0',
    type: 'prediction',
    dependencies: ['ai_analytics'],
    healthCheck: async () => {
      try {
        await aiAnalyticsService.getPredictionAnalysis()
        return true
      } catch {
        return false
      }
    },
    metadata: {
      predictionTypes: ['sales', 'demand', 'trend', 'userGrowth', 'systemFailure']
    }
  })

  // 注册AI异常检测功能
  aiCoreService.registerFeature({
    id: 'ai_anomaly',
    name: 'AI异常检测',
    description: '自动检测系统异常',
    status: 'online',
    version: '2.0.0',
    type: 'anomaly',
    healthCheck: async () => {
      return true // 模拟健康检查
    },
    metadata: {
      detectionTypes: ['order', 'payment', 'system', 'userBehavior', 'security']
    }
  })

  // 注册AI安全功能
  aiCoreService.registerFeature({
    id: 'ai_security',
    name: 'AI安全防护',
    description: '智能安全防护系统',
    status: 'online',
    version: '2.0.0',
    type: 'security',
    healthCheck: async () => {
      return true // 模拟健康检查
    },
    metadata: {
      securityTypes: ['threatDetection', 'riskAssessment', 'complianceCheck', 'vulnerabilityScan']
    }
  })

  // 注册AI内容分析功能
  aiCoreService.registerFeature({
    id: 'ai_content',
    name: 'AI内容分析',
    description: '智能内容分析和处理',
    status: 'online',
    version: '2.0.0',
    type: 'content',
    healthCheck: async () => {
      return true // 模拟健康检查
    },
    metadata: {
      contentTypes: ['reviews', 'social', 'trending', 'userGenerated', 'systemLogs']
    }
  })

  // 注册AI推荐功能
  aiCoreService.registerFeature({
    id: 'ai_recommendation',
    name: 'AI智能推荐',
    description: '个性化智能推荐系统',
    status: 'online',
    version: '2.0.0',
    type: 'recommendation',
    dependencies: ['ai_analytics'],
    healthCheck: async () => {
      return true // 模拟健康检查
    },
    metadata: {
      recommendationTypes: ['services', 'products', 'content', 'personalization', 'systemOptimization']
    }
  })

  // 注册AI学习功能
  aiCoreService.registerFeature({
    id: 'ai_learning',
    name: 'AI自主学习',
    description: '基于用户交互和系统数据的自主学习',
    status: 'online',
    version: '2.0.0',
    type: 'assistant',
    dependencies: ['ai_analytics'],
    healthCheck: async () => {
      return true // 模拟健康检查
    },
    metadata: {
      learningTypes: ['supervised', 'unsupervised', 'reinforcement', 'transfer']
    }
  })

  // 注册AI系统管理功能
  aiCoreService.registerFeature({
    id: 'ai_system_management',
    name: 'AI系统管理',
    description: '多系统统一管理和协调',
    status: 'online',
    version: '2.0.0',
    type: 'assistant',
    dependencies: ['ai_anomaly', 'ai_security'],
    healthCheck: async () => {
      return true
    },
    metadata: {
      managementTypes: ['systemMonitoring', 'configuration', 'coordination', 'inspection']
    }
  })

  // 注册AI推荐引擎功能
  aiCoreService.registerFeature({
    id: 'ai_recommendation_engine',
    name: 'AI推荐引擎',
    description: '基于用户行为和偏好的智能推荐系统',
    status: 'online',
    version: '2.1.0',
    type: 'recommendation',
    dependencies: ['ai_analytics', 'ai_prediction'],
    healthCheck: async () => {
      try {
        await aiRecommendationEngine.getRecommendations('test')
        return true
      } catch {
        return false
      }
    },
    metadata: {
      strategies: ['contentBased', 'collaborative', 'hybrid', 'popular', 'similar'],
      features: ['personalized', 'category', 'provider', 'similarServices', 'realTime']
    }
  })

  // 注册AI订单智能功能
  aiCoreService.registerFeature({
    id: 'ai_order_intelligence',
    name: 'AI订单智能',
    description: '智能订单匹配、排序和风险预测',
    status: 'online',
    version: '2.1.0',
    type: 'prediction',
    dependencies: ['ai_analytics', 'ai_recommendation'],
    healthCheck: async () => {
      try {
        await aiOrderIntelligence.getPendingOrdersWithSmartSorting()
        return true
      } catch {
        return false
      }
    },
    metadata: {
      capabilities: ['smartMatching', 'riskPrediction', 'providerRanking', 'autoSorting', 'statusTracking']
    }
  })

  // 注册AI图像分析功能
  aiCoreService.registerFeature({
    id: 'ai_image_analysis',
    name: 'AI图像分析',
    description: '基于transformers的本地图像分析服务',
    status: 'online',
    version: '2.1.0',
    type: 'content',
    healthCheck: async () => {
      const status = aiImageAnalysis.getModelStatus()
      return status.classifier || status.detector || status.captioner
    },
    metadata: {
      capabilities: ['classification', 'objectDetection', 'captioning', 'faceDetection', 'safetyDetection'],
      models: ['resnet-50', 'yolov5s', 'blip-image-captioning']
    }
  })

  // 注册AI头像验证功能
  aiCoreService.registerFeature({
    id: 'ai_avatar_verification',
    name: 'AI头像验证',
    description: '服务提供者头像智能验证，检测胸牌和人脸唯一性',
    status: 'online',
    version: '2.1.0',
    type: 'security',
    dependencies: ['ai_image_analysis'],
    healthCheck: async () => {
      const stats = aiAvatarVerification.getStats()
      return stats.totalVerifications >= 0
    },
    metadata: {
      capabilities: ['faceDetection', 'badgeDetection', 'logoRecognition', 'uniquenessCheck', 'antiFraud'],
      badgePattern: {
        shape: 'circle',
        color: 'blue',
        logoText: '伴',
        position: ['left', 'right', 'center']
      },
      cooldownSeconds: 300
    }
  })

  console.log('All AI features registered successfully')

  // 启动系统巡查
  setTimeout(() => {
    aiCoreService.startSystemInspection()
  }, 5000) // 5秒后开始第一次巡查

  console.log('System inspection scheduled')
}